import { useEffect, useState } from "react";
import { UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Lightweight technician management — Admin (all partners) + Partner (own).
export default function FieldTechnicians() {
  const [data, setData] = useState(null);
  const [partners, setPartners] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", mobile: "", partner_id: "" });
  const [mobileEdit, setMobileEdit] = useState(null); // {id, value}
  const [busy, setBusy] = useState(false);
  const [role, setRole] = useState(null);

  const load = () => api.get("/field/technicians").then(({ data }) => setData(data.technicians));
  useEffect(() => {
    load().catch((e) => toast.error(e.response?.data?.detail || "Not permitted"));
    api.get("/auth/me").then(({ data }) => {
      setRole(data.role);
      if (data.role === "admin") {
        api.get("/admin/partners?partner_type=pws_franchisee").then(({ data }) =>
          setPartners(data.partners || data || [])).catch(() => {});
      }
    }).catch(() => {});
  }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/field/technicians", form);
      toast.success("Technician added — they can now sign in at /field/login");
      setForm({ name: "", email: "", mobile: "", partner_id: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not add technician"); }
    finally { setBusy(false); }
  };

  const saveMobile = async (t) => {
    try {
      await api.put(`/field/technicians/${t.id}`, { mobile: mobileEdit.value });
      toast.success("Mobile saved — job alerts will now also go by SMS");
      setMobileEdit(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };

  const [pinEdit, setPinEdit] = useState(null); // {id, value}

  const savePin = async (t) => {
    const v = (pinEdit.value || "").trim();
    if (!/^\d{4}$/.test(v)) { toast.error("PIN must be exactly 4 digits"); return; }
    try {
      await api.put(`/field/technicians/${t.id}`, { pin: v });
      toast.success(`PIN set for ${t.name} — they can now sign in at /field/login`);
      setPinEdit(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not set PIN"); }
  };

  const setActive = async (t, active) => {
    try {
      const { data: r } = await api.put(`/field/technicians/${t.id}`, { active });
      if (!active && r.future_assigned_appointments > 0) {
        toast.warning(`${t.name} still has ${r.future_assigned_appointments} future assigned job(s) — please reassign them.`);
      } else {
        toast.success(active ? "Technician reactivated" : "Technician deactivated");
      }
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };

  const [revoking, setRevoking] = useState(null); // tech id mid-request
  const revokeSession = async (t) => {
    if (!window.confirm(
      `Force ${t.name} to sign in again on their phone?\n\nThey stay active and keep their jobs — this just ends their current session so a lost or borrowed phone can't keep using it.`
    )) return;
    setRevoking(t.id);
    try {
      await api.post(`/field/technicians/${t.id}/revoke-session`);
      toast.success(`${t.name} has been signed out — they'll need their PIN to get back in`);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not sign them out"); }
    finally { setRevoking(null); }
  };

  // Jun 2026 — explicit SMS / email job-alert toggles per technician.
  const setNotify = async (t, key, value) => {
    try {
      await api.put(`/field/technicians/${t.id}`, { [key]: value });
      toast.success(`${key === "notify_sms" ? "SMS" : "Email"} job alerts ${value ? "on" : "off"} for ${t.name}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8 max-w-2xl mx-auto" data-testid="field-technicians-page">
      <div className="flex items-center gap-2">
        <Users className="w-6 h-6 text-cyan-600" />
        <h1 className="text-xl font-bold text-slate-900">Technicians</h1>
      </div>
      <p className="mt-1 text-xs text-slate-500">Field staff sign in with their <b>4-digit PIN</b> at <b>/field/login</b> and only see the Water Health Checks assigned to them.</p>

      <form onSubmit={add} className="mt-5 rounded-2xl bg-white border border-slate-200 p-4 grid sm:grid-cols-2 gap-3">
        <input data-testid="tech-add-name" required placeholder="Full name" value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm" />
        <input data-testid="tech-add-email" required type="email" placeholder="Work email" value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm" />
        <input data-testid="tech-add-mobile" type="tel" placeholder="Mobile (for job alert texts)" value={form.mobile}
          onChange={(e) => setForm({ ...form, mobile: e.target.value })}
          className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm sm:col-span-2" />
        {role === "admin" && (
          <select data-testid="tech-add-partner" required value={form.partner_id}
            onChange={(e) => setForm({ ...form, partner_id: e.target.value })}
            className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm sm:col-span-2 bg-white">
            <option value="">Select partner…</option>
            {partners.map((p) => <option key={p.id} value={p.id}>{p.business_name}</option>)}
          </select>
        )}
        <button data-testid="tech-add-submit" disabled={busy}
          className="sm:col-span-2 py-2.5 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold text-sm flex items-center justify-center gap-1.5 disabled:opacity-50">
          <UserPlus className="w-4 h-4" /> ADD TECHNICIAN
        </button>
      </form>

      <div className="mt-5 space-y-2" data-testid="tech-list">
        {(data || []).map((t) => (
          <div key={t.id} data-testid={`tech-row-${t.id}`} className="rounded-2xl bg-white border border-slate-200 p-4 flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-bold text-slate-900">{t.name}</div>
              <div className="text-xs text-slate-500">{t.email}{t.partner_name ? ` · ${t.partner_name}` : ""}</div>
              {mobileEdit?.id === t.id ? (
                <form className="mt-1.5 flex items-center gap-1.5" onSubmit={(e) => { e.preventDefault(); saveMobile(t); }}>
                  <input data-testid={`tech-mobile-input-${t.id}`} type="tel" autoFocus placeholder="+61 4xx xxx xxx"
                    value={mobileEdit.value} onChange={(e) => setMobileEdit({ id: t.id, value: e.target.value })}
                    className="rounded-lg border border-slate-300 px-2 py-1 text-xs w-36" />
                  <button data-testid={`tech-mobile-save-${t.id}`} className="text-[11px] font-bold text-cyan-700">SAVE</button>
                  <button type="button" onClick={() => setMobileEdit(null)} className="text-[11px] text-slate-400">Cancel</button>
                </form>
              ) : (
                <button data-testid={`tech-mobile-${t.id}`} onClick={() => setMobileEdit({ id: t.id, value: t.mobile || "" })}
                  className="mt-0.5 text-xs text-cyan-700 hover:underline">
                  {t.mobile ? `SMS: ${t.mobile}` : "+ Add mobile for SMS alerts"}
                </button>
              )}
              {role === "admin" && (pinEdit?.id === t.id ? (
                <form className="mt-1.5 flex items-center gap-1.5" onSubmit={(e) => { e.preventDefault(); savePin(t); }}>
                  <input data-testid={`tech-pin-input-${t.id}`} type="password" inputMode="numeric" autoFocus
                    placeholder="4-digit PIN" maxLength={4}
                    value={pinEdit.value} onChange={(e) => setPinEdit({ id: t.id, value: e.target.value.replace(/\D/g, "") })}
                    className="rounded-lg border border-slate-300 px-2 py-1 text-xs w-24" />
                  <button data-testid={`tech-pin-save-${t.id}`} className="text-[11px] font-bold text-cyan-700">SAVE PIN</button>
                  <button type="button" onClick={() => setPinEdit(null)} className="text-[11px] text-slate-400">Cancel</button>
                </form>
              ) : (
                <button data-testid={`tech-pin-${t.id}`} onClick={() => setPinEdit({ id: t.id, value: "" })}
                  className="mt-0.5 block text-xs text-cyan-700 hover:underline">
                  {t.has_pin ? "Field PIN: •••• (reset)" : "+ Set field sign-in PIN"}
                </button>
              ))}
              {/* Jun 2026 — notification toggles. Email alerts always have an
                  address; SMS alerts also need a mobile number on file. */}
              <div className="mt-2 flex items-center gap-2" data-testid={`tech-notify-${t.id}`}>
                <button
                  data-testid={`tech-notify-sms-${t.id}`}
                  onClick={() => setNotify(t, "notify_sms", !t.notify_sms)}
                  title={!t.mobile ? "Add a mobile number for SMS alerts to send" : ""}
                  className={`text-[10px] font-bold px-2.5 py-1 rounded-full border transition-colors ${
                    t.notify_sms
                      ? "border-cyan-300 bg-cyan-50 text-cyan-800"
                      : "border-slate-200 bg-slate-50 text-slate-400 line-through"
                  }`}
                >
                  SMS ALERTS {t.notify_sms ? "ON" : "OFF"}
                </button>
                <button
                  data-testid={`tech-notify-email-${t.id}`}
                  onClick={() => setNotify(t, "notify_email", !t.notify_email)}
                  className={`text-[10px] font-bold px-2.5 py-1 rounded-full border transition-colors ${
                    t.notify_email
                      ? "border-cyan-300 bg-cyan-50 text-cyan-800"
                      : "border-slate-200 bg-slate-50 text-slate-400 line-through"
                  }`}
                >
                  EMAIL ALERTS {t.notify_email ? "ON" : "OFF"}
                </button>
                {t.notify_sms && !t.mobile && (
                  <span className="text-[10px] text-amber-600 font-medium">needs mobile</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${t.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>
                {t.active ? "Active" : "Inactive"}
              </span>
              {t.active && (
                <button data-testid={`tech-revoke-${t.id}`} disabled={revoking === t.id}
                  onClick={() => revokeSession(t)} title="Ends their current sign-in without deactivating them"
                  className="text-[11px] font-bold px-3 py-1.5 rounded-full border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-50">
                  {revoking === t.id ? "SIGNING OUT…" : "FORCE SIGN-OUT"}
                </button>
              )}
              <button data-testid={`tech-toggle-${t.id}`} onClick={() => setActive(t, !t.active)}
                className="text-[11px] font-bold px-3 py-1.5 rounded-full border border-slate-300 text-slate-700 hover:bg-slate-50">
                {t.active ? "DEACTIVATE" : "REACTIVATE"}
              </button>
            </div>
          </div>
        ))}
        {data && data.length === 0 && <div className="text-center text-sm text-slate-400 py-8">No technicians yet.</div>}
      </div>
    </div>
  );
}
